import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  hisIntegrationApi,
  hisIntegrationQueryKeys,
  isGoLive,
  isSandbox,
  type CatalogEntityType,
  type HisType,
} from "../../shared/api/hisIntegration";
import { apiError } from "../../shared/api/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import {
  Select as UiSelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table as UiTable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { describeSyncFailure } from "./hisSyncFailure";
import { TimeInput } from "../../shared/ui/TimeInput";

const STT_SAMEDAY = [
  ["EXTERNAL_HIS", "HIS bên thứ 3 cấp"],
  ["ADDY", "Addy tự cấp"],
];
const STT_FUTURE = [
  ["ADDY_NOW", "Addy cấp ngay khi đặt"],
  ["PUSH_TO_HIS", "Addy cấp + đẩy số sang HIS"],
  ["HIS_MORNING_SYNC", "HIS cấp khi đồng bộ sáng ngày khám"],
  ["HIS_AT_DELIVERY", "HIS cấp khi gửi đơn"],
];
const STT_RESET = [
  ["PER_SESSION", "Theo từng buổi (Sáng/Chiều)"],
  ["DAILY", "Theo ngày"],
];
const WRITE_MODE = [
  ["OPTIMISTIC", "Nhận đơn ngay (store-and-forward)"],
  ["STRICT", "Bắt buộc xác nhận trực tiếp"],
];
const DELIVERY_MODE = [
  ["AUTO", "Tự động (hệ thống nền tự đẩy)"],
  ["MANUAL", "Thủ công (nhân viên bấm đẩy từng lịch)"],
];

function StatusPill({ status, action }: { status: string; action?: "BOOK" | "CANCEL" }) {
  const variant: BadgeProps["variant"] =
    status === "CONFIRMED" || status === "SYNCED"
      ? // A confirmed CANCEL isn't the same "good news" as a confirmed BOOK — same info/blue
        // distinction as the appointment detail page's "Thông tin HIS" panel (#18676).
        action === "CANCEL"
        ? "info"
        : "success"
      : status === "FAILED" || status === "REJECTED"
        ? "destructive"
        : "warning";
  return <Badge variant={variant}>{status}</Badge>;
}

/** "Hành động": which verb this outbound_booking row is reporting on (#18676) — without this the
 * "Lịch đã sync" table has no way to tell a cancel-confirmed row apart from a booking-confirmed
 * one (both just show a green CONFIRMED pill; only the empty STT column hints at the difference). */
function ActionPill({ action }: { action: "BOOK" | "CANCEL" }) {
  return (
    <Badge variant={action === "CANCEL" ? "info" : "secondary"}>
      {action === "CANCEL" ? "Hủy lịch" : "Đặt lịch"}
    </Badge>
  );
}

/** ADR-0020 — Kết nối HIS bên thứ 3 (chọn đơn vị, chính sách STT, credential, giám sát đồng bộ). */
export function HisIntegrationPage() {
  const qc = useQueryClient();
  const providersQ = useQuery({
    queryKey: hisIntegrationQueryKeys.providers(),
    queryFn: () => hisIntegrationApi.providers(),
  });
  const bindingQ = useQuery({
    queryKey: hisIntegrationQueryKeys.binding(),
    queryFn: () => hisIntegrationApi.get(),
  });

  const [hisType, setHisType] = useState<HisType>("NATIVE");
  const [sttSourceSameday, setSttSourceSameday] = useState("EXTERNAL_HIS");
  const [sttFuture, setSttFuture] = useState("ADDY_NOW");
  const [sttSyncTime, setSttSyncTime] = useState("06:00");
  const [sttReset, setSttReset] = useState("PER_SESSION");
  const [writeMode, setWriteMode] = useState("OPTIMISTIC");
  const [deliveryMode, setDeliveryMode] = useState<"AUTO" | "MANUAL">("AUTO");
  const [enabled, setEnabled] = useState(true);
  const [creds, setCreds] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<"bookings" | "patients" | "logs" | "catalog">("bookings");
  const [catalogEntity, setCatalogEntity] = useState<CatalogEntityType>("DEPARTMENT");
  const [bookingActionFilter, setBookingActionFilter] = useState<"ALL" | "BOOK" | "CANCEL">("ALL");

  useEffect(() => {
    const b = bindingQ.data;
    if (!b) return;
    setHisType(b.hisType);
    setSttSourceSameday(b.sttSourceSameday);
    setSttFuture(b.sttFuture);
    setSttSyncTime((b.sttSyncTime ?? "06:00").slice(0, 5));
    setSttReset(b.sttReset);
    setWriteMode(b.writeMode);
    setDeliveryMode(b.deliveryMode);
    setEnabled(b.enabled);
    // baseUrl is a non-secret field (see CredentialField.secret) and the backend already returns
    // it in plaintext via GET — pre-fill it so it isn't blank every time this tab is revisited.
    // Secret fields (secretKey, token, x-leo-environment) intentionally stay blank: the backend
    // never returns them, and the label already tells staff "để trống nếu giữ nguyên".
    setCreds(b.baseUrl ? { baseUrl: b.baseUrl } : {});
  }, [bindingQ.data]);

  const provider = useMemo(
    () => providersQ.data?.find((p) => p.code === hisType),
    [providersQ.data, hisType],
  );

  // Once metadata has been synced the tenant is committed to this HIS → the provider select locks;
  // only connect/disconnect + re-sync remain. `connected` = enabled = allow booking / push to HIS.
  const boundHisType = bindingQ.data?.hisType ?? "NATIVE";
  // A synced external tenant is normally locked to its HIS. A sandbox is the exception: the whole
  // point of rehearsing is to go live afterwards, and rehearsing necessarily syncs a catalog, so
  // locking on "synced" would punish exactly the clinics that used the sandbox as intended.
  const locked =
    !!bindingQ.data?.synced && boundHisType !== "NATIVE" && !isSandbox(boundHisType);
  const connected = !!bindingQ.data?.enabled && boundHisType !== "NATIVE";

  const setConnected = useMutation({
    mutationFn: (on: boolean) =>
      on ? hisIntegrationApi.connect() : hisIntegrationApi.disconnect(),
    onSuccess: () => qc.invalidateQueries({ queryKey: hisIntegrationQueryKeys.binding() }),
    onError: (e) => setError(apiError(e).detail),
  });
  const metadataSync = useMutation({
    mutationFn: () => hisIntegrationApi.runMetadataSync(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: hisIntegrationQueryKeys.binding() });
      qc.invalidateQueries({ queryKey: ["his-integration", "catalog-synced"] });
      qc.invalidateQueries({ queryKey: ["his-integration", "locations"] });
    },
    // #19086 — a 5xx here used to surface the backend's generic English "An unexpected error
    // occurred" under the status line. Say it in Vietnamese and give the reference id to quote.
    onError: (e) => {
      const err = apiError(e);
      setError(
        err.status >= 500
          ? `Đồng bộ gặp lỗi hệ thống, chưa hoàn tất.${typeof err.problem?.correlationId === "string" ? ` Mã tra cứu: ${err.problem.correlationId}.` : ""} Vui lòng thử lại hoặc báo quản trị.`
          : err.detail,
      );
    },
  });

  const save = useMutation({
    mutationFn: () =>
      hisIntegrationApi.update({
        hisType,
        baseUrl: creds.baseUrl,
        sttSourceSameday,
        sttFuture,
        sttSyncTime,
        sttReset,
        writeMode,
        deliveryMode,
        enabled,
        credentials: Object.keys(creds).length ? creds : undefined,
      }),
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: hisIntegrationQueryKeys.binding() });
    },
    onError: (e) => setError(apiError(e).detail),
  });

  const bookings = useQuery({
    queryKey: hisIntegrationQueryKeys.bookings(),
    queryFn: () => hisIntegrationApi.syncedBookings(),
  });
  const patients = useQuery({
    queryKey: hisIntegrationQueryKeys.patients(),
    queryFn: () => hisIntegrationApi.syncedPatients(),
  });
  const logs = useQuery({
    queryKey: hisIntegrationQueryKeys.logs(),
    queryFn: () => hisIntegrationApi.logs(),
  });


  const catalogSynced = useQuery({
    queryKey: hisIntegrationQueryKeys.catalogSynced(catalogEntity),
    queryFn: () => hisIntegrationApi.catalogSynced(catalogEntity),
    enabled: tab === "catalog",
  });

  const toggleOverride = useMutation({
    mutationFn: (vars: { id: string; overridden: boolean }) =>
      hisIntegrationApi.setCatalogOverride(catalogEntity, vars.id, vars.overridden),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: hisIntegrationQueryKeys.catalogSynced(catalogEntity) });
    },
  });

  return (
    <div className="grid max-w-4xl gap-4">
      <div>
        <h1 className="text-xl font-semibold text-foreground">Kết nối HIS</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Chọn đơn vị HIS backing cho phòng khám. Addy đóng vai trò trung gian cho mini app và giám
          sát toàn bộ dữ liệu truyền nhận.
        </p>
      </div>

      {/* Chọn đơn vị HIS */}
      <Card>
        <CardContent className="grid gap-3 pt-6">
          <div className="grid gap-1.5">
            <Label htmlFor="his-provider">Đơn vị cung cấp HIS</Label>
            <UiSelect
              value={hisType}
              disabled={locked}
              onValueChange={(v) => {
                const next = v as HisType;
                const b = bindingQ.data;
                // Backend rejects an external-to-external switch outright (must go through
                // "Không có" first) — this mirrors that rule client-side so the
                // mistake is caught before a doomed "Lưu" round-trip, not instead of the backend
                // check.
                const goLive = b ? isGoLive(b.hisType, next) : false;
                if (
                  b &&
                  !goLive &&
                  b.hisType !== "NATIVE" &&
                  next !== "NATIVE" &&
                  next !== b.hisType
                ) {
                  setError(
                    `Đang kết nối ${b.hisType}. Chọn "Không có" và lưu để ngắt kết nối trước khi đổi sang đơn vị khác.`,
                  );
                  return;
                }
                if (goLive) {
                  const ok = confirm(
                    `Chuyển từ sandbox sang hệ thống thật (${next})? Danh mục khoa/phòng/dịch vụ đã đồng bộ từ sandbox sẽ bị ẩn và phải đồng bộ lại từ HIS thật. Từ lúc này lịch hẹn sẽ được đẩy vào bệnh viện thật.`,
                  );
                  if (!ok) return;
                }
                if (b && b.hisType !== "NATIVE" && next === "NATIVE") {
                  const ok = confirm(
                    `Ngắt kết nối ${b.hisType}? Toàn bộ khoa/phòng khám/dịch vụ đã đồng bộ từ ${b.hisType} sẽ bị ẩn (không xoá — có thể khôi phục nếu kết nối lại ${b.hisType} và đồng bộ lại).`,
                  );
                  if (!ok) return;
                }
                setError(null);
                setHisType(next);
                // Switching to a DIFFERENT provider than the one currently saved must not carry
                // that provider's baseUrl into this one's input — it isn't this provider's data,
                // even though both happen to have a field keyed "baseUrl". Only re-show it when
                // switching back to the provider that's actually bound.
                // A sandbox carries its own address, so show it rather than an empty box — the
                // clinic should be able to read which server it is about to talk to. The bound
                // provider's saved baseUrl still wins when switching back to it.
                const fallback = providersQ.data?.find((p) => p.code === next)?.defaultBaseUrl;
                setCreds(
                  b && next === b.hisType && b.baseUrl
                    ? { baseUrl: b.baseUrl }
                    : fallback
                      ? { baseUrl: fallback }
                      : {},
                );
              }}
            >
              <SelectTrigger id="his-provider">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {providersQ.data?.map((p) => (
                  <SelectItem
                    key={p.code}
                    value={p.code}
                    disabled={
                      !!bindingQ.data &&
                      bindingQ.data.hisType !== "NATIVE" &&
                      p.code !== "NATIVE" &&
                      p.code !== bindingQ.data.hisType &&
                      !isGoLive(bindingQ.data.hisType, p.code)
                    }
                  >
                    {p.displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </UiSelect>
            {locked && (
              <p className="m-0 text-xs text-muted-foreground">
                Đã kết nối và đồng bộ với {bindingQ.data?.hisType} — không thể đổi HIS. Dùng nút{" "}
                <strong>Ngắt kết nối</strong> để tạm dừng cho phép đặt lịch.
              </p>
            )}
          </div>

          {provider && provider.external && (
            <div className="grid gap-2.5">
              {provider.credentialFields.map((f) => (
                <div key={f.key} className="grid gap-1.5">
                  <Label htmlFor={`cred-${f.key}`}>
                    {f.label}
                    {bindingQ.data?.hasCredentials && f.secret
                      ? " (đã lưu — để trống nếu giữ nguyên)"
                      : ""}
                  </Label>
                  <Input
                    id={`cred-${f.key}`}
                    type={f.secret ? "password" : "text"}
                    autoComplete={f.secret ? "new-password" : "off"}
                    value={creds[f.key] ?? ""}
                    placeholder={f.secret ? "••••••" : `Nhập ${f.label.toLowerCase()}`}
                    onChange={(e) => setCreds((c) => ({ ...c, [f.key]: e.target.value }))}
                  />
                </div>
              ))}
            </div>
          )}
          {provider && (
            <div className="text-[13px] text-muted-foreground">
              Khả năng: {provider.capabilities.join(", ")}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Chính sách STT */}
      <Card>
        <CardContent className="grid gap-3 pt-6">
          <h3 className="text-[15px] font-semibold text-foreground">Chính sách số thứ tự (STT)</h3>
          <Select
            label="STT trong ngày (same-day)"
            value={sttSourceSameday}
            onChange={setSttSourceSameday}
            options={STT_SAMEDAY}
          />
          <Select
            label="STT đặt lịch tương lai"
            value={sttFuture}
            onChange={setSttFuture}
            options={STT_FUTURE}
          />
          {sttFuture === "HIS_MORNING_SYNC" && (
            <div className="grid max-w-[200px] gap-1.5">
              <Label htmlFor="stt-sync-time">Giờ đồng bộ STT buổi sáng</Label>
              <TimeInput id="stt-sync-time" value={sttSyncTime} onChange={setSttSyncTime} />
            </div>
          )}
          <Select label="Reset STT" value={sttReset} onChange={setSttReset} options={STT_RESET} />
          <Select
            label="Chế độ ghi (khi mất kết nối HIS)"
            value={writeMode}
            onChange={setWriteMode}
            options={WRITE_MODE}
          />
          <Select
            label="Chế độ đẩy lịch sang HIS"
            value={deliveryMode}
            onChange={(v) => setDeliveryMode(v as "AUTO" | "MANUAL")}
            options={DELIVERY_MODE}
          />
          <p className="m-0 -mt-1.5 text-xs text-muted-foreground">
            {deliveryMode === "AUTO"
              ? "Tự động: hệ thống nền tự đẩy mọi lịch sang HIS (tách biệt khỏi thao tác trên web) — không có nút đẩy thủ công."
              : "Thủ công: lịch mới tạo sẽ có nút “Đồng bộ” để nhân viên tự đẩy sang HIS; hệ thống không tự đẩy."}
          </p>
          {/* Trạng thái kết nối */}
          {boundHisType !== "NATIVE" && (
            <div className="flex items-center gap-2 text-sm" data-testid="his-connection-status">
              <span className="text-muted-foreground">Trạng thái:</span>
              {connected ? (
                <span className="inline-flex items-center gap-1 font-medium text-emerald-600">
                  <span className="h-2 w-2 rounded-full bg-emerald-500" /> Đang kết nối (cho phép đặt lịch)
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 font-medium text-amber-600">
                  <span className="h-2 w-2 rounded-full bg-amber-500" /> Đã ngắt — không cho đặt lịch mới
                </span>
              )}
            </div>
          )}
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex flex-wrap items-center gap-2">
            <Button disabled={save.isPending} onClick={() => save.mutate()}>
              {save.isPending ? "Đang lưu…" : "Lưu cấu hình"}
            </Button>
            {/* Nút 1 — Kết nối / Ngắt kết nối (chỉ bật/tắt cho phép đặt lịch, không đổi HIS) */}
            {boundHisType !== "NATIVE" && (
              <Button
                variant={connected ? "outline" : "default"}
                disabled={setConnected.isPending}
                onClick={() => setConnected.mutate(!connected)}
              >
                {setConnected.isPending
                  ? "Đang xử lý…"
                  : connected
                    ? "Ngắt kết nối"
                    : "Kết nối"}
              </Button>
            )}
            {/* Nút 2 — Đồng bộ metadata (khoa/phòng/dịch vụ + tỉnh/phường + ánh xạ Geo). KHÔNG gồm
                bệnh nhân & lịch hẹn — những dữ liệu đó tra/đẩy theo từng bản ghi. */}
            {boundHisType !== "NATIVE" && connected && (
              <Button
                variant="secondary"
                disabled={metadataSync.isPending}
                onClick={() => metadataSync.mutate()}
                title="Kéo danh mục (khoa/phòng/dịch vụ), địa chỉ (tỉnh/phường) và ánh xạ Geo từ HIS về Addy"
              >
                {metadataSync.isPending ? "Đang đồng bộ…" : "Đồng bộ dữ liệu từ HIS"}
              </Button>
            )}
            {metadataSync.data && (
              // #18884 — this used to read `ran`, which is only "a sync was attempted", so a sync
              // that never reached the HIS printed "Khoa 0 · Phòng 0 · Dịch vụ 0" as if the clinic
              // had an empty catalogue. Zero is still allowed to mean zero: SUCCESS_EMPTY says so
              // in words instead of leaving the reader to guess from the numbers.
              <span
                className={
                  metadataSync.data.status === "FAILED"
                    ? "self-center text-[13px] font-medium text-destructive"
                    : "self-center text-[13px] text-muted-foreground"
                }
              >
                {metadataSync.data.status === "FAILED"
                  ? `Không lấy được dữ liệu từ HIS. Dữ liệu danh mục hiện có không bị thay đổi. Lý do: ${describeSyncFailure(metadataSync.data.failureReason)}. Xem đường dẫn đã gọi ở tab Nhật ký truyền nhận.`
                  : metadataSync.data.status === "NOT_RUN"
                    ? "Chưa chạy — cần đang kết nối."
                    : metadataSync.data.status === "SUCCESS_EMPTY"
                      ? `HIS trả về danh mục rỗng (0 khoa, 0 phòng, 0 dịch vụ) · Phường/xã ${metadataSync.data.wards} · Ánh xạ Geo ${metadataSync.data.geoMapped}`
                      : `Khoa ${metadataSync.data.departments} · Phòng ${metadataSync.data.rooms} · Dịch vụ ${metadataSync.data.services} · Phường/xã ${metadataSync.data.wards} · Ánh xạ Geo ${metadataSync.data.geoMapped}`}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Giám sát đồng bộ */}
      <Card>
        <CardContent className="grid gap-3 pt-6">
          <div className="flex flex-wrap gap-1.5">
            {(
              [
                ["bookings", "Lịch đã sync"],
                ["patients", "Bệnh nhân đã sync"],
                ["catalog", "Danh mục đã sync"],
                ["logs", "Nhật ký truyền nhận"],
              ] as const
            ).map(([k, label]) => (
              <Button
                key={k}
                size="sm"
                variant={tab === k ? "default" : "ghost"}
                onClick={() => setTab(k)}
              >
                {label}
              </Button>
            ))}
          </div>

          {tab === "bookings" && (
            <div className="grid gap-2">
              <div className="flex flex-wrap gap-1.5">
                {(
                  [
                    ["ALL", "Tất cả"],
                    ["BOOK", "Đặt lịch"],
                    ["CANCEL", "Hủy lịch"],
                  ] as const
                ).map(([k, label]) => (
                  <Button
                    key={k}
                    size="sm"
                    variant={bookingActionFilter === k ? "default" : "ghost"}
                    onClick={() => setBookingActionFilter(k)}
                  >
                    {label}
                  </Button>
                ))}
              </div>
              <Table
                head={["Ngày khám", "Kết nối", "Hành động", "STT", "Trạng thái", "Thử", "Lỗi"]}
                empty="Chưa có lịch nào được đồng bộ."
                rows={(bookings.data ?? [])
                  .filter((b) => bookingActionFilter === "ALL" || b.action === bookingActionFilter)
                  .map((b) => [
                    b.bookingDate ?? "—",
                    b.connector,
                    <ActionPill action={b.action} />,
                    b.action === "CANCEL" ? "—" : (b.stt ?? "—"),
                    <StatusPill status={b.status} action={b.action} />,
                    b.attempts,
                    <span title={b.lastError ?? ""}>{(b.lastError ?? "").slice(0, 40) || "—"}</span>,
                  ])}
              />
            </div>
          )}
          {tab === "patients" &&
            ((patients.data ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Chưa có bệnh nhân nào được đồng bộ.
              </p>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {(patients.data ?? []).map((p, i) => (
                  <div key={i} className="rounded-lg border bg-card p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-foreground">{p.fullName}</span>
                      <StatusPill status={p.status} />
                    </div>
                    <div className="mt-1 grid gap-0.5 text-sm text-muted-foreground">
                      <div>Mã BN: {p.patientCode}</div>
                      <div>Kết nối: {p.connector}</div>
                      <div>Mã HIS: {p.hisPatientCode ?? "—"}</div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          {tab === "catalog" && (
            <div className="grid gap-3">
              <div className="flex flex-wrap gap-1.5">
                {(
                  [
                    ["DEPARTMENT", "Khoa"],
                    ["EXAM_ROOM", "Phòng khám"],
                    ["SERVICE", "Dịch vụ"],
                  ] as const
                ).map(([k, label]) => (
                  <Button
                    key={k}
                    size="sm"
                    variant={catalogEntity === k ? "default" : "ghost"}
                    onClick={() => setCatalogEntity(k)}
                  >
                    {label}
                  </Button>
                ))}
              </div>
              {(catalogSynced.data ?? []).length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {catalogSynced.isLoading
                    ? "Đang tải…"
                    : "Chưa có dữ liệu nào được đồng bộ. Bấm \"Đồng bộ danh mục\" ở trên."}
                </p>
              ) : (
                <UiTable>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tên</TableHead>
                      <TableHead>Mã HIS</TableHead>
                      <TableHead>Nguồn</TableHead>
                      <TableHead>Trạng thái</TableHead>
                      <TableHead>Đồng bộ lúc</TableHead>
                      <TableHead>Khóa chỉnh sửa thủ công</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(catalogSynced.data ?? []).map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>{row.name}</TableCell>
                        <TableCell>{row.externalCode}</TableCell>
                        <TableCell>{row.sourceSystem}</TableCell>
                        <TableCell>
                          <Badge variant={row.active ? "success" : "warning"}>
                            {row.active ? "Đang hoạt động" : "Đã ẩn (HIS không còn trả về)"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {row.syncedAt ? new Date(row.syncedAt).toLocaleString("vi-VN") : "—"}
                        </TableCell>
                        <TableCell>
                          <label className="flex items-center gap-2">
                            <Checkbox
                              checked={row.locallyOverridden}
                              disabled={toggleOverride.isPending}
                              onCheckedChange={(v) =>
                                toggleOverride.mutate({ id: row.id, overridden: v === true })
                              }
                            />
                            <span className="text-[13px] text-muted-foreground">
                              Giữ nguyên tên/giá đã sửa tay
                            </span>
                          </label>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </UiTable>
              )}
            </div>
          )}
          {tab === "logs" && (
            <Table
              head={["Thời gian", "Kết nối", "Thao tác", "OK", "Lỗi", "Đường dẫn", "ms"]}
              empty="Chưa có lượt truyền nhận nào."
              rows={(logs.data ?? []).map((e) => [
                new Date(e.createdAt).toLocaleString("vi-VN"),
                e.connector,
                e.operation,
                e.ok ? "✓" : "✗",
                e.errorCode ?? "—",
                e.endpoint ?? "—",
                e.latencyMs ?? "—",
              ])}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Select({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[][];
}) {
  return (
    <div className="grid gap-1.5">
      <Label>{label}</Label>
      <UiSelect value={value} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map(([v, l]) => (
            <SelectItem key={v} value={v}>
              {l}
            </SelectItem>
          ))}
        </SelectContent>
      </UiSelect>
    </div>
  );
}

function Table({
  head,
  rows,
  empty,
}: {
  head: string[];
  rows: React.ReactNode[][];
  empty: string;
}) {
  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground">{empty}</p>;
  }
  return (
    <UiTable>
      <TableHeader>
        <TableRow>
          {head.map((h) => (
            <TableHead key={h}>{h}</TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r, i) => (
          <TableRow key={i}>
            {r.map((c, j) => (
              <TableCell key={j}>{c}</TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </UiTable>
  );
}
